// src/AdvancedPlayerStats.js
import React, { useState, useEffect, useMemo, useContext } from "react";
import { calculateTotals, formatTime } from "./GameStatsTableHelper";
import { AuthContext } from "./App";

export default function AdvancedPlayerStats({ teamId }) {
  const { accessToken } = useContext(AuthContext);

  const [stats, setStats] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teamTotalsByGame, setTeamTotalsByGame] = useState({});
  const [loading, setLoading] = useState(true);

  const [sortConfig, setSortConfig] = useState({ key: "total_points", direction: "desc" });
  const [sortConfigPer40, setSortConfigPer40] = useState({ key: "total_points", direction: "desc" });

  useEffect(() => {
    if (!teamId || !accessToken) return;

    async function fetchStats() {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/teams/${teamId}/player_stats_with_team_totals`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!res.ok) throw new Error("Failed to fetch stats");

        const data = await res.json();

        setStats(Array.isArray(data.players) ? data.players : []);
        setTeamTotalsByGame(data.team_totals_by_game || {});

        const uniquePlayers = Array.from(
          new Map(
            (data.players || []).map((s) => [
              s.player_id,
              { id: s.player_id, player_name: s.player_name, team_id: s.team_id },
            ])
          ).values()
        );
        setPlayers(uniquePlayers);
      } catch (err) {
        console.error(err);
        setStats([]);
        setPlayers([]);
        setTeamTotalsByGame({});
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [teamId, accessToken]);

  const statsByPlayer = useMemo(() => {
    return players.map((player) => {
      const playerGames = stats.filter((s) => s.player_id === player.id);
      const totals = calculateTotals(playerGames, teamTotalsByGame);

      const totalSeconds = totals.minutes || 0;

      const gamesPlayed = playerGames.filter(
        (g) => (g.minutes || 0) * 60 + (g.seconds || 0) > 0
      ).length;

      const mpgSeconds = gamesPlayed > 0 ? totalSeconds / gamesPlayed : 0;

      const net_rating = Number(
        ((totals.off_rating ?? 0) - (totals.def_rating ?? 0)).toFixed(1)
      );

      const pm_per_game =
        gamesPlayed > 0 ? Number((totals.bpm / gamesPlayed).toFixed(1)) : 0;

      return {
        id: player.id,
        player_name: player.player_name,
        ...totals,
        total_seconds: totalSeconds,
        gp: gamesPlayed,
        mpg_seconds: mpgSeconds,
        net_rating,
        pm_per_game,
      };
    });
  }, [players, stats, teamTotalsByGame]);

  const handleSort = (key, isPer40 = false) => {
    const setter = isPer40 ? setSortConfigPer40 : setSortConfig;
    const config = isPer40 ? sortConfigPer40 : sortConfig;

    setter({
      key,
      direction: config.key === key && config.direction === "desc" ? "asc" : "desc",
    });
  };

  const sortedPlayers = useMemo(() => {
    return [...statsByPlayer].sort((a, b) => {
      const getValue = (p, key) => {
        if (key === "player_name") return (p.player_name || "").toLowerCase();
        if (key === "mpg") return p.mpg_seconds;
        return p[key] ?? 0;
      };
      const aVal = getValue(a, sortConfig.key);
      const bVal = getValue(b, sortConfig.key);

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [statsByPlayer, sortConfig]);

  const sortedPlayersPer40 = useMemo(() => {
    return [...statsByPlayer]
      .map((p) => {
        const factor = p.total_seconds > 0 ? (40 * 60) / p.total_seconds : 0;
        const per40 = {};
        Object.keys(p).forEach((k) => {
          if (typeof p[k] === "number") per40[k] = Number((p[k] * factor).toFixed(1));
        });

        per40.treb = Number((
          ((p.off_rebounds || 0) + (p.def_rebounds || 0)) * factor
        ).toFixed(1));

        return { ...p, per40 };
      })
      .sort((a, b) => {
        const aVal = a.per40[sortConfigPer40.key] ?? 0;
        const bVal = b.per40[sortConfigPer40.key] ?? 0;

        if (aVal < bVal) return sortConfigPer40.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfigPer40.direction === "asc" ? 1 : -1;
        return 0;
      });
  }, [statsByPlayer, sortConfigPer40]);

  if (loading) return <p>Loading stats...</p>;
  if (!statsByPlayer.length) return <p>No stats available for this team.</p>;

  const mainHeaders = [
    { key: "player_name", label: "Player" },
    { key: "gp", label: "GP" },
    { key: "mpg", label: "MPG" },
    { key: "two_fg_attempts", label: "2FGA" },
    { key: "two_fg_made", label: "2FGM" },
    { key: "three_fg_attempts", label: "3FGA" },
    { key: "three_fg_made", label: "3FGM" },
    { key: "three_pt_percentage", label: "3P%" },
    { key: "ft_attempts", label: "FTA" },
    { key: "ft_made", label: "FTM" },
    { key: "ft_percentage", label: "FT%" },
    { key: "total_points", label: "PTS" },
    { key: "fg_percentage", label: "FG%" },
    { key: "efg_percentage", label: "eFG%" },
    { key: "ts_percentage", label: "TS%" },
    { key: "tov_percentage", label: "TO%" },
    { key: "usg_percentage", label: "USG%" },
    { key: "off_rating", label: "OffRtg" },
    { key: "def_rating", label: "DefRtg" },
    { key: "net_rating", label: "NetRtg" },
    { key: "pm_per_game", label: "PM/G" },
  ];

  const per40Headers = [
    { key: "player_name", label: "Player" },
    { key: "total_points", label: "PTS" },
    { key: "fg_made", label: "FGM" },
    { key: "fg_attempts", label: "FGA" },
    { key: "three_fg_made", label: "3PM" },
    { key: "three_fg_attempts", label: "3PA" },
    { key: "ft_made", label: "FTM" },
    { key: "ft_attempts", label: "FTA" },
    { key: "off_rebounds", label: "OREB" },
    { key: "def_rebounds", label: "DREB" },
    { key: "treb", label: "TREB" },
    { key: "assists", label: "AST" },
    { key: "steals", label: "STL" },
    { key: "blocks", label: "BLK" },
    { key: "turnovers", label: "TO" },
    { key: "fouls", label: "FOUL" },
  ];

  return (
    <div className="overflow-x-auto space-y-8">

      {/* Advanced Stats */}
      <table className="table-auto border-collapse border border-gray-300 w-full text-sm shadow-sm rounded">
        <thead>
          <tr className="bg-gray-100">
            {mainHeaders.map((h) => (
              <th
                key={h.key}
                className="border px-2 py-2 text-left cursor-pointer select-none font-medium"
                onClick={() => handleSort(h.key)}
              >
                {h.label}
                {sortConfig.key === h.key && (sortConfig.direction === "asc" ? " 🔼" : " 🔽")}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedPlayers.map((p) => (
            <tr key={p.id} className="hover:bg-gray-50">
              {mainHeaders.map((h) => (
                <td key={h.key} className="border px-2 py-1">
                  {h.key === "mpg" ? formatTime(p.mpg_seconds) : p[h.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Section divider row */}
      <table className="table-auto border-collapse border border-gray-300 w-full text-sm shadow-sm rounded">
        <thead>
          <tr>
            <td
              colSpan={per40Headers.length}
              className="bg-gray-50 font-semibold text-center py-3 uppercase tracking-wide border-b"
            >
              Per 40 Minutes Played
            </td>
          </tr>

          <tr className="bg-gray-100">
            {per40Headers.map((h) => (
              <th
                key={h.key}
                className="border px-2 py-2 text-left cursor-pointer select-none font-medium"
                onClick={() => handleSort(h.key, true)}
              >
                {h.label}
                {sortConfigPer40.key === h.key &&
                  (sortConfigPer40.direction === "asc" ? " 🔼" : " 🔽")}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedPlayersPer40.map((p) => (
            <tr key={p.id} className="hover:bg-gray-50">
              {per40Headers.map((h) => (
                <td key={h.key} className="border px-2 py-1 text-center">
                  {h.key === "player_name" ? p.player_name : p.per40[h.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
